import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';

import {
  ChipItem,
  ChipItemClose,
  ChipItemFacet,
  ChipItemValuesList,
  ChipItemValueItem,
} from '@src/design-system/chip';

import {
  ShowSpecificNumberContext,
  ShowSpecificNumberExtra,
  ShowSpecificNumberItem,
} from '@src/design-system/show-specific-number';

import { useTableViewHelpers } from '@design-system/molecules/ticlick-table/hooks/use-table-view-helpers';
import { useTableStates } from '@design-system/molecules/ticlick-table/hooks/use-table-states';

import { getFilterConfigByFacetName } from '@shared/features/views/helpers';
import { FILTER_KIND } from '@shared/features/views/types/filter-config';

import { STATIC_FILTER_ENTITY_TYPES } from '@shared/features/views/components/entities-filters/config';

import { formatTextForDisplay } from '@src/shared/utils/helpers/format-text-for-display';
import { FILTER_NONE_LABEL } from '@shared/features/views/helpers/filter-none';

import { extractDateFieldname } from './helpers/extract-date-fieldname';

import {
  ChipLeadCalendlyTypeValue,
  ChipLabelValue,
  ChipLeadReferalValue,
  ChipLeadStatusValue,
  ChipMemberValue,
  ChipProgramValue,
  ChipPartnerValue,
} from './chip-value-renderers';

import type { TView } from '@shared/types/view';

const MAX_ITEMS = 2;

const FILTER_VALUE_RENDERERS = {
  [STATIC_FILTER_ENTITY_TYPES.member]: ChipMemberValue,
  [STATIC_FILTER_ENTITY_TYPES.label]: ChipLabelValue,
  [STATIC_FILTER_ENTITY_TYPES.leadStatus]: ChipLeadStatusValue,
  [STATIC_FILTER_ENTITY_TYPES.program]: ChipProgramValue,
  [STATIC_FILTER_ENTITY_TYPES.leadReferal]: ChipLeadReferalValue,
  [STATIC_FILTER_ENTITY_TYPES.leadSource]: ChipLeadCalendlyTypeValue,
  [STATIC_FILTER_ENTITY_TYPES.partner]: ChipPartnerValue,
} as const;

type FilterValueProps = {
  entityType?: string;
  field: string;
  value: string | number | null;
  /** The filter's `friendlyValue` for this value, when it set one — e.g. a
   * member's name for a member-id filter. */
  label?: string;
  filterColor?: string;
};

function simpleFilterOptionLabel(
  field: string,
  value: string | number
): string | null {
  const config = getFilterConfigByFacetName(field);
  if (config?.kind !== FILTER_KIND.simple) return null;

  return (
    config.options.find((option) => option.value === String(value))?.label ??
    null
  );
}

function FilterValue({
  entityType,
  field,
  value,
  label,
  filterColor,
}: FilterValueProps) {
  const { clearOneValue } = useTableViewHelpers();

  if (value === null) {
    return (
      <ChipItemValueItem
        className='text-xs px-0'
        style={{
          backgroundColor: 'transparent',
        }}
      >
        <div
          className='size-2 rounded-full bg-active-border'
          style={{ backgroundColor: filterColor }}
        />
        {FILTER_NONE_LABEL}
        <ChipItemClose onClick={() => clearOneValue(field, value)} />
      </ChipItemValueItem>
    );
  }

  const Renderer =
    FILTER_VALUE_RENDERERS[entityType as keyof typeof FILTER_VALUE_RENDERERS];

  if (Renderer) {
    return (
      <ChipItemValueItem
        className='text-xs px-0'
        style={{
          backgroundColor: 'transparent',
        }}
      >
        <div
          className='size-2 rounded-full bg-active-border'
          style={{ backgroundColor: filterColor }}
        />
        <Renderer
          fieldName={field}
          id={value}
        />
        <ChipItemClose onClick={() => clearOneValue(field, value)} />
      </ChipItemValueItem>
    );
  }

  const text =
    simpleFilterOptionLabel(field, value) ??
    label ??
    formatTextForDisplay(String(value));

  return (
    <ChipItemValueItem
      className='text-xs px-0'
      style={{
        backgroundColor: 'transparent',
      }}
    >
      <div
        className='size-2 rounded-full'
        style={{ backgroundColor: filterColor }}
      />
      {text}
      <ChipItemClose onClick={() => clearOneValue(field, value)} />
    </ChipItemValueItem>
  );
}

export const ActiveFilterChip = ({
  staticFilter,
}: {
  staticFilter: TView['static_filters'][number];
}) => {
  const { clearOneValue } = useTableViewHelpers();
  const { currentView } = useTableStates();

  const chipRef = useRef<HTMLDivElement>(null);
  const [chipItemWidth, setChipItemWidth] = useState<number>();

  const { field, value, entityType, friendlyField, friendlyValue } =
    staticFilter;

  const filterColor = useMemo(
    () => getFilterConfigByFacetName(field, currentView?.entity_type)?.color,
    [field, currentView?.entity_type]
  );

  const overflowItems = useMemo(() => value.slice(MAX_ITEMS), [value]);

  useLayoutEffect(() => {
    if (chipRef.current) {
      setChipItemWidth(chipRef.current.clientWidth);
    }
  }, []);

  const stylesAccToConfigColor = useMemo(
    () => ({
      backgroundColor: filterColor ? `${filterColor}20` : undefined,
      borderColor: filterColor ? `${filterColor}10` : undefined,
      color: filterColor
        ? `color-mix(in srgb, ${filterColor} 50%, black)`
        : undefined,
    }),
    [filterColor]
  );

  if (entityType === STATIC_FILTER_ENTITY_TYPES.date) {
    return (
      <ChipItem
        className='bg-primary-100'
        style={stylesAccToConfigColor}
      >
        <ChipItemFacet>
          {formatTextForDisplay(extractDateFieldname(field))}
        </ChipItemFacet>

        <ChipItemValuesList className='item-center'>
          <ChipItemValueItem
            style={{
              backgroundColor: 'transparent',
            }}
          >
            <div
              className='size-2 rounded-full bg-active-border'
              style={{ backgroundColor: filterColor }}
            />
            {friendlyValue?.[0]}
            <ChipItemClose onClick={() => clearOneValue(field, value[0])} />
          </ChipItemValueItem>
        </ChipItemValuesList>
      </ChipItem>
    );
  }

  return (
    <ShowSpecificNumberContext maxItems={MAX_ITEMS}>
      <ChipItem
        style={stylesAccToConfigColor}
        className='bg-primary-100'
        ref={chipRef}
      >
        <ChipItemFacet>
          {formatTextForDisplay(friendlyField ?? field)}
        </ChipItemFacet>

        <ChipItemValuesList className='item-center'>
          {value.map((item, index) => (
            <ShowSpecificNumberItem key={`${field}-${item}`}>
              <FilterValue
                entityType={entityType}
                field={field}
                value={item}
                label={friendlyValue?.[index]}
                filterColor={filterColor}
              />
            </ShowSpecificNumberItem>
          ))}

          {overflowItems.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <ShowSpecificNumberExtra
                  style={{
                    backgroundColor: filterColor
                      ? `${filterColor}15`
                      : undefined,
                    color: filterColor
                      ? `color-mix(in srgb, ${filterColor} 50%, black)`
                      : undefined,
                  }}
                  className='inline-flex cursor-pointer items-center gap-1 rounded-sm bg-primary-50 px-2 py-[2px] font-medium text-gray-800'
                />
              </PopoverTrigger>

              <PopoverContent
                align='end'
                alignOffset={-4}
                sideOffset={8}
                style={{
                  ...(chipItemWidth && {
                    maxWidth: chipItemWidth,
                  }),
                }}
                className='flex w-fit flex-wrap gap-2 p-1'
              >
                {overflowItems.map((item) => (
                  <FilterValue
                    key={`${field}-overflow-${item}`}
                    entityType={entityType}
                    field={field}
                    value={item}
                    filterColor={filterColor}
                  />
                ))}
              </PopoverContent>
            </Popover>
          )}
        </ChipItemValuesList>
      </ChipItem>
    </ShowSpecificNumberContext>
  );
};
